import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer,
  FormControl, Grid, IconButton, InputLabel, LinearProgress, MenuItem, Paper, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getAgents } from '../services/agent.service';
import { assignLead, autoAssignLeads, createLead, deleteLead, getLeads, setLeadLabels, updateLead, updateLeadStatus } from '../services/lead.service';
import { getLabels } from '../services/chat.service';
import LabelMultiSelect from '../components/LabelMultiSelect';
import WhatsAppAccountSelect from '../components/WhatsAppAccountSelect';
import useLeadStatuses from '../hooks/useLeadStatuses';
import { LEAD_COURSES, LEAD_SOURCES } from '../constants/leadFilterOptions';

const sources = LEAD_SOURCES;
const priorities = ['low', 'medium', 'high'];
const courses = LEAD_COURSES;
const studentTypes = ['New Student', 'Existing Student', 'Returning Student'];

const initialForm = {
  name: '', phone: '', email: '', source: 'Manual Entry', status: 'New', priority: 'medium',
  assignedAgentId: '', courseInterested: 'Forex', batchInterested: '', budget: '',
  studentType: 'New Student', notes: '', followUpDate: '', labelIds: []
};

function agentName(agent) {
  if (!agent) return 'Unassigned';
  return agent.name || [agent.firstName, agent.lastName].filter(Boolean).join(' ') || agent.email;
}

function toInputDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
}

function toForm(lead) {
  return {
    name: lead?.name || '',
    phone: lead?.phone || '',
    email: lead?.email || '',
    source: lead?.source || 'Manual Entry',
    status: lead?.status || 'New',
    priority: lead?.priority || 'medium',
    assignedAgentId: lead?.assignedAgent?.id || '',
    courseInterested: lead?.courseInterested || 'Forex',
    batchInterested: lead?.batchInterested || '',
    budget: lead?.budget || '',
    studentType: lead?.studentType || 'New Student',
    notes: lead?.notes || '',
    followUpDate: toInputDate(lead?.followUpDate),
    labelIds: (lead?.labels || []).map((item) => item.id)
  };
}

function toPayload(form) {
  const { labelIds, ...fields } = form;
  return {
    ...fields,
    assignedAgentId: form.assignedAgentId || null,
    budget: form.budget === '' ? null : Number(form.budget),
    followUpDate: form.followUpDate ? new Date(form.followUpDate).toISOString() : null
  };
}

function LeadsPage() {
  const leadStatuses = useLeadStatuses();
  const statuses = leadStatuses.map((status) => status.name);
  const navigate = useNavigate();
  const { socket } = useOutletContext() || {};
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [labels, setLabels] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const emptyFilters = { search: '', phone: '', email: '', status: '', source: '', assignedAgentId: '', courseInterested: '', whatsappAccountId: '', labelIds: [], labelMode: 'any', hasNoLabels: false, dateType: 'createdAt', dateFrom: '', dateTo: '' };
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [savingStatus, setSavingStatus] = useState({});

  const query = useMemo(() => ({
    page: pagination.page,
    limit: pagination.limit,
    search: filters.search || undefined,
    phone: filters.phone || undefined,
    email: filters.email || undefined,
    status: filters.status || undefined,
    source: filters.source || undefined,
    assignedAgentId: filters.assignedAgentId || undefined,
    courseInterested: filters.courseInterested || undefined,
    whatsappAccountId: filters.whatsappAccountId || undefined,
    labelIds: filters.labelIds.length ? filters.labelIds.join(',') : undefined,
    labelMode: filters.labelMode,
    hasNoLabels: filters.hasNoLabels || undefined,
    dateType: filters.dateType || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined
  }), [filters, pagination.page, pagination.limit]);

  const loadLeads = async () => {
    try {
      setLoading(true); setError('');
      const response = await getLeads(query);
      setLeads(response.data.data.leads || []);
      setPagination(response.data.data.pagination || pagination);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load leads.');
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    const response = await getAgents();
    setAgents(response.data.data || []);
  };

  useEffect(() => { loadAgents(); }, []);
  useEffect(() => { getLabels().then((response) => setLabels(response.data.data || [])).catch(() => {}); }, []);
  useEffect(() => { loadLeads(); }, [query]);
  useEffect(() => {
    if (!socket) return undefined;
    const applyLeadEvent = (payload = {}) => {
      if (payload.leadId == null) return;
      const status = leadStatuses.find((item) => item.code === payload.statusCode);
      const owner = payload.ownerId == null
        ? null
        : agents.find((agent) => String(agent.id) === String(payload.ownerId));
      const patchLead = (lead) => String(lead.id) !== String(payload.leadId) ? lead : {
        ...lead,
        ...(payload.statusCode ? { statusCode: payload.statusCode, status: payload.status?.name || status?.name || lead.status } : {}),
        ...(Object.prototype.hasOwnProperty.call(payload, 'ownerId') ? { assignedAgent: owner || null } : {}),
        updatedAt: payload.updatedAt || lead.updatedAt
      };
      setLeads((rows) => rows.map(patchLead));
      setProfile((current) => current ? patchLead(current) : current);
    };
    const applyLabelEvent = (payload = {}) => {
      if (payload.leadId == null || !Array.isArray(payload.labelIds)) return;
      const ids = new Set(payload.labelIds.map(String));
      const nextLabels = labels.filter((label) => ids.has(String(label.id)));
      const patchLead = (lead) => String(lead.id) === String(payload.leadId)
        ? { ...lead, labels: nextLabels }
        : lead;
      setLeads((rows) => rows.map(patchLead));
      setProfile((current) => current ? patchLead(current) : current);
      // Re-run server-side label filters so additions/removals change membership too.
      loadLeads();
    };
    socket.on('lead.updated', applyLeadEvent);
    socket.on('lead.status.changed', applyLeadEvent);
    socket.on('lead.agent.changed', applyLeadEvent);
    socket.on('crm.labels.changed', applyLabelEvent);
    return () => {
      socket.off('lead.updated', applyLeadEvent);
      socket.off('lead.status.changed', applyLeadEvent);
      socket.off('lead.agent.changed', applyLeadEvent);
      socket.off('crm.labels.changed', applyLabelEvent);
    };
  }, [socket, agents, labels, query]);

  const setFilter = (field) => (event) => {
    setFilters((current) => ({ ...current, [field]: event.target.value }));
    setPagination((current) => ({ ...current, page: 1 }));
  };

  const openCreate = () => { setEditing(null); setForm(initialForm); setDialogOpen(true); };
  const openEdit = (lead) => { setEditing(lead); setForm(toForm(lead)); setDialogOpen(true); };
  const convertToStudent = (lead) => {
    navigate('/students', {
      state: {
        openCreate: true,
        source: 'lead',
        lead: { ...lead, id: lead.id },
        contact: lead.contact || {
          firstName: String(lead.name || '').split(' ')[0] || '',
          lastName: String(lead.name || '').split(' ').slice(1).join(' '),
          phone: lead.phone,
          email: lead.email
        }
      }
    });
  };

  const saveLead = async () => {
    try {
      setError('');
      if (editing) {
        await updateLead(editing.id, toPayload(form));
        if (editing.conversationId) await setLeadLabels(editing.id, form.labelIds.map((id) => ({ id })));
        setSuccess('Lead updated.');
      } else {
        await createLead(toPayload(form));
        setSuccess('Lead added.');
      }
      setDialogOpen(false);
      await loadLeads();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save lead.');
    }
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`Delete ${lead.name || lead.phone}?`)) return;
    await deleteLead(lead.id);
    setSuccess('Lead deleted.');
    if (profile?.id === lead.id) setProfile(null);
    await loadLeads();
  };

  const reassignLead = async (lead, agentId) => {
    try {
      const assignedAgentId = agentId ? Number(agentId) : null;
      await assignLead(lead.id, { assignedAgentId, note: assignedAgentId ? 'Manual reassignment from CRM UI' : 'Unassigned from CRM UI' });
      setLeads((rows) => rows.map((row) => String(row.id) === String(lead.id)
        ? { ...row, assignedAgent: agents.find((agent) => String(agent.id) === String(assignedAgentId)) || null }
        : row));
      setSuccess(assignedAgentId ? 'Lead assigned.' : 'Lead unassigned.');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update lead assignment.');
    }
  };

  const runAutoAssign = async () => {
    try {
      const response = await autoAssignLeads({ limit: 25 });
      setSuccess(`Round-robin assigned ${response.data.data.assignedCount} lead(s).`);
      await loadLeads();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to auto-assign leads.');
    }
  };

  const changeStatus = async (lead, statusCode) => {
    const previous = { status: lead.status, statusCode: lead.statusCode };
    const next = leadStatuses.find((status) => status.code === statusCode);
    setLeads((rows) => rows.map((row) => row.id === lead.id ? { ...row, status: next.name, statusCode } : row));
    setSavingStatus((current) => ({ ...current, [lead.id]: true }));
    try {
      await updateLeadStatus(lead.id, { statusCode, expectedCurrentStatusCode: previous.statusCode });
      setSuccess(`Status updated to ${next.name}.`);
    } catch (err) {
      setLeads((rows) => rows.map((row) => row.id === lead.id ? { ...row, ...previous } : row));
      setError(err.response?.data?.message || 'Unable to update lead status.');
    } finally {
      setSavingStatus((current) => ({ ...current, [lead.id]: false }));
    }
  };

  const resetFilters = () => { setFilters(emptyFilters); setPagination((current) => ({ ...current, page: 1 })); };

  return (
    <Stack spacing={2.5}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 2.5, border: '1px solid #e8edf2' }} elevation={0}>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
          <TextField label="Search name, phone, email" value={filters.search} onChange={setFilter('search')} fullWidth />
          <TextField label="Phone" value={filters.phone} onChange={setFilter('phone')} sx={{ minWidth: 150 }} />
          <TextField label="Email" value={filters.email} onChange={setFilter('email')} sx={{ minWidth: 190 }} />
          <FormControl sx={{ minWidth: 150 }}><InputLabel>Status</InputLabel><Select label="Status" value={filters.status} onChange={setFilter('status')}><MenuItem value="">All</MenuItem>{statuses.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl>
          <FormControl sx={{ minWidth: 160 }}><InputLabel>Source</InputLabel><Select label="Source" value={filters.source} onChange={setFilter('source')}><MenuItem value="">All</MenuItem>{sources.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl>
          <FormControl sx={{ minWidth: 170 }}><InputLabel>Agent</InputLabel><Select label="Agent" value={filters.assignedAgentId} onChange={setFilter('assignedAgentId')}><MenuItem value="">All</MenuItem>{agents.map((agent) => <MenuItem key={agent.id} value={agent.id}>{agentName(agent)}</MenuItem>)}</Select></FormControl>
          <FormControl sx={{ minWidth: 170 }}><InputLabel>Course</InputLabel><Select label="Course" value={filters.courseInterested} onChange={setFilter('courseInterested')}><MenuItem value="">All</MenuItem>{courses.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl>
          <WhatsAppAccountSelect value={filters.whatsappAccountId} onChange={(value) => { setFilters((current) => ({ ...current, whatsappAccountId: value })); setPagination((current) => ({ ...current, page: 1 })); }} allowAll sx={{ minWidth: 230 }} />
          <Box sx={{ minWidth: 280, flex: 1 }}><LabelMultiSelect options={labels} value={filters.labelIds} onChange={(labelIds) => { setFilters((current) => ({ ...current, labelIds, hasNoLabels: false })); setPagination((current) => ({ ...current, page: 1 })); }} onOptionsChange={setLabels} /></Box>
          <FormControl sx={{ minWidth: 180 }}><InputLabel>Label match</InputLabel><Select label="Label match" value={filters.hasNoLabels ? 'no_labels' : filters.labelMode} onChange={(event) => { const value = event.target.value; setFilters((current) => ({ ...current, hasNoLabels: value === 'no_labels', labelMode: value === 'no_labels' ? 'any' : value, labelIds: value === 'no_labels' ? [] : current.labelIds })); }}><MenuItem value="any">Has any selected</MenuItem><MenuItem value="all">Has all selected</MenuItem><MenuItem value="no_labels">Has no labels</MenuItem></Select></FormControl>
          <FormControl sx={{ minWidth: 190 }}><InputLabel>Date Type</InputLabel><Select label="Date Type" value={filters.dateType} onChange={setFilter('dateType')}><MenuItem value="createdAt">Lead Created Date</MenuItem><MenuItem value="updatedAt">Last Updated Date</MenuItem><MenuItem value="convertedAt">Registration Date</MenuItem></Select></FormControl>
          <TextField label="From" type="date" value={filters.dateFrom} onChange={setFilter('dateFrom')} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
          <TextField label="To" type="date" value={filters.dateTo} onChange={setFilter('dateTo')} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
          <Button variant="outlined" onClick={loadLeads}>Apply</Button>
          <Button variant="text" onClick={resetFilters}>Reset Filters</Button>
          <Button variant="outlined" startIcon={<AutoFixHighIcon />} onClick={runAutoAssign}>Auto Assign</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ bgcolor: '#128c7e' }}>Add Lead</Button>
        </Stack>
      </Paper>

      {(filters.dateFrom || filters.dateTo || filters.status || filters.assignedAgentId) && <Stack direction="row" gap={1} flexWrap="wrap">
        {(filters.dateFrom || filters.dateTo) && <Chip label={`${filters.dateType === 'convertedAt' ? 'Registration' : filters.dateType === 'updatedAt' ? 'Updated' : 'Created'} Date: ${filters.dateFrom || 'Any'} - ${filters.dateTo || 'Any'}`} />}
        {filters.status && <Chip label={`Status: ${filters.status}`} />}
        {filters.assignedAgentId && <Chip label={`Agent: ${agentName(agents.find((agent) => String(agent.id) === String(filters.assignedAgentId)))}`} />}
      </Stack>}

      <Paper sx={{ border: '1px solid #e8edf2', overflow: 'hidden' }} elevation={0}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead><TableRow><TableCell>Lead</TableCell><TableCell>Labels</TableCell><TableCell>Status</TableCell><TableCell>Source</TableCell><TableCell>Course</TableCell><TableCell>Agent</TableCell><TableCell>Created</TableCell><TableCell>Chat</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {leads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell><Typography fontWeight={800}>{lead.name || 'Unnamed lead'}</Typography><Typography variant="body2" color="text.secondary">{lead.phone} {lead.email ? `• ${lead.email}` : ''}</Typography></TableCell>
                  <TableCell><Stack direction="row" gap={.5} flexWrap="wrap" sx={{ minWidth: 130 }}>{(lead.labels || []).map((item) => <Chip key={item.id} size="small" label={item.name} sx={{ height: 22, bgcolor: `${item.color || '#25d366'}22`, border: '1px solid', borderColor: item.color || '#25d366' }} />)}{!(lead.labels || []).length && <Typography variant="caption" color="text.secondary">None</Typography>}</Stack></TableCell>
                  <TableCell><Stack direction="row" alignItems="center" gap={1}><FormControl size="small" sx={{ minWidth: 145 }} disabled={savingStatus[lead.id]}><Select value={lead.statusCode || 'new'} onChange={(event) => changeStatus(lead, event.target.value)}>{leadStatuses.map((status) => <MenuItem key={status.id} value={status.code}>{status.name}</MenuItem>)}</Select></FormControl>{savingStatus[lead.id] && <CircularProgress size={18} />}</Stack></TableCell>
                  <TableCell>{lead.source || '-'}</TableCell>
                  <TableCell>{lead.courseInterested || '-'}</TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth sx={{ minWidth: 160 }}>
                      <Select value={lead.assignedAgent?.id || ''} displayEmpty onChange={(event) => reassignLead(lead, event.target.value)}>
                        <MenuItem value="">Unassigned</MenuItem>
                        {agents.map((agent) => <MenuItem key={agent.id} value={agent.id}>{agentName(agent)}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>{lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : '-'}</TableCell>
                  <TableCell><Tooltip title={lead.conversationId ? 'Open linked conversation' : 'No chat conversation found'}><span><Button size="small" startIcon={<ChatBubbleOutlineIcon />} disabled={!lead.conversationId} onClick={() => navigate(`/chat?conversationId=${lead.conversationId}`)}>Open Chat</Button></span></Tooltip></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" justifyContent="flex-end" flexWrap="wrap">
                      <Button size="small" onClick={() => navigate(`/call-center?leadId=${lead.id}&action=start`)}>Start Call</Button>
                      <Button size="small" onClick={() => navigate(`/call-center?leadId=${lead.id}&action=log`)}>Log Call</Button>
                      <Button size="small" onClick={() => navigate(`/call-center?leadId=${lead.id}&action=log`)}>Schedule Follow-up</Button>
                      <Button size="small" onClick={() => setProfile(lead)}>View Timeline</Button>
                    </Stack>
                    <IconButton onClick={() => setProfile(lead)}><VisibilityIcon /></IconButton>
                    <IconButton onClick={() => openEdit(lead)}><EditIcon /></IconButton>
                    <IconButton color="error" onClick={() => removeLead(lead)}><DeleteOutlineIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && leads.length === 0 && <TableRow><TableCell colSpan={9}><Box sx={{ py: 5, textAlign: 'center' }}><Typography fontWeight={800}>No leads found</Typography><Typography color="text.secondary">Add a lead or adjust filters.</Typography></Box></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={pagination.total} page={Math.max(pagination.page - 1, 0)} rowsPerPage={pagination.limit} onPageChange={(event, page) => setPagination((current) => ({ ...current, page: page + 1 }))} onRowsPerPageChange={(event) => setPagination({ ...pagination, page: 1, limit: Number(event.target.value) })} rowsPerPageOptions={[10, 20, 50, 100]} />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Lead' : 'Add Lead'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}><TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Batch Interested" value={form.batchInterested} onChange={(e) => setForm({ ...form, batchInterested: e.target.value })} fullWidth /></Grid>
            <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>Source</InputLabel><Select label="Source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>{sources.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>Status</InputLabel><Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{statuses.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>Priority</InputLabel><Select label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>{priorities.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>Course</InputLabel><Select label="Course" value={form.courseInterested} onChange={(e) => setForm({ ...form, courseInterested: e.target.value })}>{courses.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} sm={4}><FormControl fullWidth><InputLabel>Student Type</InputLabel><Select label="Student Type" value={form.studentType} onChange={(e) => setForm({ ...form, studentType: e.target.value })}>{studentTypes.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} sm={4}><TextField label="Budget" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} fullWidth /></Grid>
            <Grid item xs={12} sm={6}><FormControl fullWidth><InputLabel>Assigned Agent</InputLabel><Select label="Assigned Agent" value={form.assignedAgentId} onChange={(e) => setForm({ ...form, assignedAgentId: e.target.value })}><MenuItem value="">Unassigned</MenuItem>{agents.map((agent) => <MenuItem key={agent.id} value={agent.id}>{agentName(agent)}</MenuItem>)}</Select></FormControl></Grid>
            <Grid item xs={12} sm={6}><TextField label="Follow-up Date" type="datetime-local" value={form.followUpDate} onChange={(e) => setForm({ ...form, followUpDate: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth /></Grid>
            <Grid item xs={12}><TextField label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} multiline minRows={4} fullWidth /></Grid>
            {editing?.conversationId && <Grid item xs={12}><LabelMultiSelect options={labels} value={form.labelIds} onChange={(labelIds) => setForm({ ...form, labelIds })} onOptionsChange={setLabels} /></Grid>}
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setDialogOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.phone.trim()} onClick={saveLead}>Save</Button></DialogActions>
      </Dialog>

      <Drawer anchor="right" open={!!profile} onClose={() => setProfile(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, p: 3 } }}>
        {profile && <Stack spacing={2}>
          <Box><Typography variant="h5" fontWeight={850}>{profile.name || 'Unnamed lead'}</Typography><Typography color="text.secondary">{profile.phone} {profile.email ? `• ${profile.email}` : ''}</Typography></Box>
          <Divider />
          <Stack direction="row" gap={1} flexWrap="wrap"><Chip label={profile.status || '-'} /><Chip label={profile.priority || '-'} /><Chip label={profile.source || '-'} /></Stack>
          <Box><Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Labels</Typography><LabelMultiSelect options={labels} value={(profile.labels || []).map((item) => item.id)} onOptionsChange={setLabels} onChange={async (labelIds) => { try { const response = await setLeadLabels(profile.id, labelIds.map((id) => ({ id }))); const updated = response.data.data; setProfile(updated); setLeads((rows) => rows.map((row) => String(row.id) === String(updated.id) ? updated : row)); } catch (err) { setError(err.response?.data?.message || 'Unable to update labels.'); } }} disabled={!profile.conversationId} helperText={!profile.conversationId ? 'Labels become available after a canonical conversation exists.' : ''} /></Box>
          <Box><Typography variant="subtitle2" color="text.secondary">Assigned Agent</Typography><Typography>{agentName(profile.assignedAgent)}</Typography></Box>
          <Box><Typography variant="subtitle2" color="text.secondary">Course and Batch</Typography><Typography>{profile.courseInterested || '-'} / {profile.batchInterested || '-'}</Typography></Box>
          <Box><Typography variant="subtitle2" color="text.secondary">Budget and Student Type</Typography><Typography>{profile.budget || '-'} / {profile.studentType || '-'}</Typography></Box>
          <Box><Typography variant="subtitle2" color="text.secondary">Follow-up Date</Typography><Typography>{profile.followUpDate ? new Date(profile.followUpDate).toLocaleString() : '-'}</Typography></Box>
          <Box><Typography variant="subtitle2" color="text.secondary">Notes</Typography><Typography sx={{ whiteSpace: 'pre-wrap' }}>{profile.notes || 'No notes yet.'}</Typography></Box>
          <Stack direction="row" spacing={1} flexWrap="wrap"><Button variant="contained" startIcon={<PersonAddAltIcon />} onClick={() => convertToStudent(profile)}>Convert to Student</Button><Button variant="outlined" startIcon={<EditIcon />} onClick={() => openEdit(profile)}>Edit</Button><Button variant="outlined" startIcon={<PersonAddAltIcon />} onClick={() => openEdit(profile)}>Reassign</Button></Stack>
        </Stack>}
      </Drawer>
    </Stack>
  );
}

export default LeadsPage;
